// ============================================================================
// TILED MAP LOADER
// Loads maps exported from Tiled Map Editor (JSON format)
// ============================================================================

import CONFIG from '../config.js';

export class TiledMapLoader {
    /**
     * Load a Tiled JSON map file
     * @param {string} mapPath - Path to the Tiled JSON map file
     * @returns {Promise<Object>} Parsed map data
     */
    static async loadMap(mapPath) {
        try {
            // Resolve path relative to current page location
            // This ensures it works from any directory structure (file://, http://, subdirectories)
            const finalPath = mapPath.startsWith('/') || mapPath.startsWith('http://') || mapPath.startsWith('https://') 
                ? mapPath 
                : new URL(mapPath, window.location.href).href;
            
            console.log('Loading map from path:', mapPath, '(resolved to:', finalPath, ')');
            console.log('Current page URL:', window.location.href);
            
            const response = await fetch(finalPath);
            
            // Read response as text first to check what we got
            const responseText = await response.text();
            const contentType = response.headers.get('content-type') || '';
            
            // Check if we got HTML (404 page) instead of JSON
            // Some servers return 200 OK with HTML error pages, so check content, not just status
            const trimmedText = responseText.trim();
            const isHTML = contentType.includes('text/html') || 
                          trimmedText.startsWith('<!DOCTYPE') || 
                          trimmedText.startsWith('<!doctype') ||
                          trimmedText.startsWith('<html') ||
                          trimmedText.startsWith('<HTML');
            
            if (!response.ok || isHTML) {
                console.error('Map fetch failed. Status:', response.status, response.statusText);
                console.error('Content-Type:', contentType);
                console.error('Response preview (first 500 chars):', responseText.substring(0, 500));
                console.error('Current page URL:', window.location.href);
                console.error('Attempted path:', finalPath);
                const resolvedUrl = new URL(finalPath, window.location.href).href;
                console.error('Resolved URL would be:', resolvedUrl);
                
                if (isHTML) {
                    throw new Error(`Map file not found (404). Tried: ${finalPath}. Expected file at: ${resolvedUrl}. Make sure the file exists at that location.`);
                }
                throw new Error(`Failed to load map: ${response.status} ${response.statusText}`);
            }
            
            // Parse as JSON
            let mapData;
            try {
                mapData = JSON.parse(responseText);
            } catch (parseError) {
                console.error('Failed to parse JSON. Response preview:', responseText.substring(0, 200));
                console.error('This suggests the file exists but is not valid JSON, or we got an HTML error page');
                throw new Error(`Invalid JSON in map file: ${parseError.message}`);
            }
            
            console.log('Raw map data loaded, layers count:', mapData.layers?.length);
            const parsed = this.parseMap(mapData);
            console.log('After parsing, objects count:', parsed.objects.length);
            return parsed;
        } catch (error) {
            console.error('Error loading Tiled map:', error);
            throw error;
        }
    }

    /**
     * Parse Tiled JSON map data into our internal format
     * @param {Object} tiledData - Raw Tiled JSON data
     * @returns {Object} Parsed map data with tiles and objects
     */
    static parseMap(tiledData) {
        const result = {
            width: tiledData.width,
            height: tiledData.height,
            tileWidth: tiledData.tilewidth,
            tileHeight: tiledData.tileheight,
            layers: [],
            objects: []
        };

        // Parse tile layers
        for (const layer of tiledData.layers) {
            console.log(`Processing layer: name="${layer.name}", type="${layer.type}", hasObjects=${!!layer.objects}`);
            if (layer.type === 'tilelayer') {
                result.layers.push({
                    name: layer.name,
                    type: 'tilelayer', // Preserve type field
                    data: layer.data,
                    width: layer.width,
                    height: layer.height,
                    visible: layer.visible !== false,
                    properties: layer.properties || {}
                });
            } else if (layer.type === 'objectgroup' || layer.objects) {
                // Object layer: either has type='objectgroup' or has an 'objects' array
                console.log(`Found object layer "${layer.name}" with ${layer.objects?.length || 0} objects`);
                // Parse object layer (for entities)
                if (layer.objects && Array.isArray(layer.objects)) {
                    for (const obj of layer.objects) {
                        result.objects.push({
                            name: obj.name,
                            type: obj.type || obj.name, // Use 'type' field or 'name' as fallback
                            x: obj.x,
                            y: obj.y,
                            width: obj.width || 0,
                            height: obj.height || 0,
                            rotation: obj.rotation || 0,
                            point: obj.point || false, // Point objects (width=0, height=0)
                            properties: this.parseProperties(obj.properties || []),
                            layer: layer.name
                        });
                    }
                }
            } else {
                console.log(`Unknown layer type: ${layer.type || 'undefined'}`);
            }
        }
        
                console.log(`Parsed ${result.objects.length} objects from map`);

                // Parse tilesets (for tile type mapping and image loading)
                result.tilesets = tiledData.tilesets.map(tileset => ({
                    firstgid: tileset.firstgid,
                    name: tileset.name,
                    tilewidth: tileset.tilewidth,
                    tileheight: tileset.tileheight,
                    image: tileset.image, // Image path
                    imagewidth: tileset.imagewidth,
                    imageheight: tileset.imageheight,
                    columns: tileset.columns, // Number of columns in tileset
                    margin: tileset.margin || 0,
                    spacing: tileset.spacing || 0,
                    tiles: tileset.tiles || [] // Custom properties per tile
                }));

                return result;
    }

    /**
     * Convert Tiled properties array to object
     * @param {Array} properties - Tiled properties array
     * @returns {Object} Properties as key-value pairs
     */
    static parseProperties(properties) {
        const result = {};
        if (Array.isArray(properties)) {
            for (const prop of properties) {
                result[prop.name] = prop.value;
            }
        } else if (typeof properties === 'object') {
            // Sometimes properties come as an object
            return properties;
        }
        return result;
    }

    /**
     * Get tile type from Tiled tile ID using tileset properties
     * @param {number} gid - Global tile ID from Tiled
     * @param {Array} tilesets - Tileset definitions
     * @returns {string} Tile type name
     */
    static getTileTypeFromGID(gid, tilesets) {
        // Find which tileset this GID belongs to
        for (let i = tilesets.length - 1; i >= 0; i--) {
            const tileset = tilesets[i];
            if (gid >= tileset.firstgid) {
                const localId = gid - tileset.firstgid;
                
                // Check if this tile has custom properties
                if (tileset.tiles && tileset.tiles[localId]) {
                    const tileData = tileset.tiles[localId];
                    if (tileData.properties) {
                        const props = this.parseProperties(tileData.properties);
                        if (props.type) {
                            return props.type;
                        }
                    }
                }
                
                // Fallback: use tileset name + local ID
                // This requires tileset naming convention
                return `${tileset.name}_${localId}`;
            }
        }
        
        return 'unknown';
    }
}
